import { collection, addDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { recipes, activities, stories } from '../data/content';

export async function migrateContentToFirestore() {
  console.log('Starting content migration...');
  
  try {
    // Get all existing content to check for duplicates
    const existingContentSnapshot = await getDocs(collection(db, 'content'));
    const existingContent = new Map();
    
    existingContentSnapshot.docs.forEach(doc => {
      const data = doc.data();
      // Use originalId if available, otherwise use title+type as key
      const key = data.originalId || `${data.type}_${data.title}`;
      existingContent.set(key, true);
    });

    if (existingContent.size > 0) {
      const confirm = window.confirm(
        `Found ${existingContent.size} existing content items in Firestore. ` +
        'The migration will skip items that already exist. Continue?'
      );
      if (!confirm) {
        console.log('Migration cancelled by user');
        return false;
      }
    }

    let migratedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;

    // Helper function to check and migrate a single item
    async function migrateItem(item, type) {
      try {
        // Use original id from content.js if available, otherwise use title+type
        const originalId = item.id || `${type}_${item.title}`;
        const duplicateKey = `${type}_${originalId}`;
        
        // Check if already exists
        if (existingContent.has(duplicateKey) || existingContent.has(originalId)) {
          console.log(`⊘ Skipped duplicate ${type}: ${item.title} (ID: ${originalId})`);
          skippedCount++;
          return false;
        }

        // Prepare data
        const contentData = {
          ...item,
          type: type,
          originalId: originalId, // Store original ID for duplicate checking
          status: 'approved',
          createdAt: serverTimestamp(),
          totalClaps: 0,
          clappedBy: {},
          commentsCount: 0
        };

        // Convert ingredients array format if needed (for recipes)
        if (type === 'recipe' && item.ingredients) {
          contentData.ingredients = item.ingredients.map(ing => {
            if (typeof ing === 'string') return ing;
            return `${ing.amount} ${ing.name}`;
          });
        }

        await addDoc(collection(db, 'content'), contentData);
        
        // Mark as migrated in our map
        existingContent.set(duplicateKey, true);
        existingContent.set(originalId, true);
        
        migratedCount++;
        console.log(`✓ Migrated ${type}: ${item.title} (ID: ${originalId})`);
        return true;
      } catch (error) {
        console.error(`✗ Error migrating ${type} "${item.title}":`, error);
        errorCount++;
        return false;
      }
    }

    // Migrate recipes
    console.log(`\n📝 Migrating ${recipes.length} recipes...`);
    for (const recipe of recipes) {
      await migrateItem(recipe, 'recipe');
    }

    // Migrate activities
    console.log(`\n🎨 Migrating ${activities.length} activities...`);
    for (const activity of activities) {
      await migrateItem(activity, 'activity');
    }

    // Migrate stories
    console.log(`\n📖 Migrating ${stories.length} stories...`);
    for (const story of stories) {
      await migrateItem(story, 'story');
    }

    console.log(`\n✅ Migration complete!`);
    console.log(`   ✓ Migrated: ${migratedCount} items`);
    console.log(`   ⊘ Skipped: ${skippedCount} duplicates`);
    console.log(`   ✗ Errors: ${errorCount} items`);
    
    return true;
  } catch (error) {
    console.error('❌ Migration failed:', error);
    return false;
  }
}

// Helper function to run migration from browser console
// Usage: window.migrateContent()
if (typeof window !== 'undefined') {
  window.migrateContent = migrateContentToFirestore;
}

